import {GateweeTableColumn} from '@gatewee/interfaces';

const priorityKeys = [
  'transactionId',
  'e2eId',
  'externalId',
  'idempotencyKey',
  'status',
  'type',
  'amount',
  'value',
  'createdAt',
  'updatedAt',
] as const;

const estimatedColWidthPx = 100;
const reservedWidthPx = 64;
const minVisibleColumns = 6;
const maxVisibleColumns = 14;

/** Fraction of viewport used for modal width/height. */
export const providerTxViewportRatio = 0.95;

export function estimateMaxVisibleColumns(containerWidthPx: number): number {
  const usable = Math.max(0, containerWidthPx - reservedWidthPx);
  const estimated = Math.floor(usable / estimatedColWidthPx);
  return Math.min(maxVisibleColumns, Math.max(minVisibleColumns, estimated));
}

export function estimateProviderTxContainerWidth(): number {
  return Math.round(window.innerWidth * providerTxViewportRatio);
}

export function sortProviderTransactionKeys(keys: string[]): string[] {
  const priorityIndex = new Map(priorityKeys.map((key, index) => [key, index]));

  return [...keys].sort((a, b) => {
    const aIdx = priorityIndex.get(a as (typeof priorityKeys)[number]);
    const bIdx = priorityIndex.get(b as (typeof priorityKeys)[number]);

    if (aIdx !== undefined && bIdx !== undefined) {
      return aIdx - bIdx;
    }
    if (aIdx !== undefined) {
      return -1;
    }
    if (bIdx !== undefined) {
      return 1;
    }
    return a.localeCompare(b);
  });
}

export type ProviderTransactionColumnPrefs = {
  field: string;
  visible: boolean;
};

export function readProviderTransactionColumnPrefs(
  tableId: string,
): ProviderTransactionColumnPrefs[] | null {
  try {
    const raw = localStorage.getItem(tableId);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as ProviderTransactionColumnPrefs[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export interface BuildProviderTransactionColumnsOptions {
  maxVisible: number;
  webhookResendEnabled: boolean;
  identifier?: string;
  savedPrefs?: ProviderTransactionColumnPrefs[] | null;
}

export function buildProviderTransactionColumns(
  keys: string[],
  options: BuildProviderTransactionColumnsOptions,
): GateweeTableColumn[] {
  const sortedKeys = sortProviderTransactionKeys(keys);
  const savedByField = new Map(
    (options.savedPrefs ?? []).map(pref => [pref.field, pref.visible]),
  );

  const columns: GateweeTableColumn[] = sortedKeys.map((key, index) => {
    const defaultVisible = index < options.maxVisible;
    const savedVisible = savedByField.get(key);

    return {
      label: key,
      field: key,
      sortable: true,
      visible: savedVisible ?? defaultVisible,
      alwaysVisible: false,
      ellipsed: true,
      class: 'gw-text-text-primary',
    };
  });

  if (
    options.identifier === 'provider-transactions' &&
    options.webhookResendEnabled
  ) {
    columns.push({
      label: 'features.recipients.providerTransactions.resendWebhook',
      field: 'actions',
      visible: true,
      alwaysVisible: true,
      actions: [
        {
          key: 'resendWebhook',
          label: 'features.recipients.providerTransactions.resendWebhook',
          icon: '@phi.arrows-clockwise',
        },
      ],
    });
  }

  return columns;
}

export function countHiddenDataColumns(columns: GateweeTableColumn[]): number {
  return columns.filter(col => col.field !== 'actions' && col.visible === false)
    .length;
}

export function countVisibleDataColumns(columns: GateweeTableColumn[]): number {
  return columns.filter(col => col.field !== 'actions' && col.visible !== false)
    .length;
}

export function countDataColumns(columns: GateweeTableColumn[]): number {
  return columns.filter(col => col.field !== 'actions').length;
}
